import { isNil } from 'lodash-es'
import React, { FunctionComponent, ReactNode, useCallback, useEffect } from 'react'

import { Box, mdBumps } from '../atoms'
import { useNotifyingState } from '../extensions'
import { TabAnchor } from './TabAnchor'
import { TabBar, TabBarProps } from './TabBar'

export interface TabItem {
  header?: string
  component: (setTabIndex: (index: number) => void) => ReactNode
}

export interface TabViewProps extends TabBarProps {
  items: TabItem[]
  onTabChange?: (index: number) => void
  className?: string
  tabIndex?: number
  headless?: boolean
}

interface TabProps {
  item: TabItem
  selected: boolean
  onClick: (index: number) => void
  index: number
}

const Tab: FunctionComponent<TabProps> = ({
  item: { header },
  index,
  selected,
  onClick,
}) => {
  const onAnchorClick = useCallback(() => onClick(index), [onClick, index])

  if (isNil(header)) return null

  return (
    <TabAnchor
      data-testid='tab-anchor'
      onClick={onAnchorClick}
      selected={selected}
      key={header}
      mr={6}
    >
      {header}
    </TabAnchor>
  )
}

export const TabView: FunctionComponent<TabViewProps> = ({
  items,
  onTabChange,
  tabIndex,
  headless,
  ...props
}) => {
  const [current, setCurrent] = useNotifyingState(
    tabIndex ?? 0,
    onTabChange ? [onTabChange] : [],
  )

  const tabs = !headless ? (
    <TabBar width={1} mb={mdBumps}>
      {items.map((item, index) => (
        <Tab
          key={index}
          index={index}
          item={item}
          selected={current === index}
          onClick={setCurrent}
        />
      ))}
    </TabBar>
  ) : null

  useEffect(() => {
    if (!isNil(tabIndex)) setCurrent(tabIndex)
  }, [tabIndex])

  return (
    <Box {...props}>
      {tabs}
      <Box width={1}>{items[current].component(setCurrent)}</Box>
    </Box>
  )
}
